import { ReactElement, useState } from "react";
import {
  FormControl, IconButton, MenuItem,
  Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip
} from "@mui/material";
import { IAcquisitionVariable, ILimit } from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getPalette, getPaletteColor } from "../../../configuration/configuration";
import StyledTableRow from "../../../components/styledTableRow";
import { Autocomplete } from "@mui/material";
import Select  from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";
import ColorPicker from "../../../components/colorPicker";

interface IProps {
  limits: ILimit[];
  setLimits: (limits: ILimit[]) => void;
  machineVariables: IAcquisitionVariable[];
}

const LimitsPanel = ({ limits, setLimits, machineVariables }: IProps): ReactElement => {
  const [ colors, setColors ] = useState<string[]>( limits.map((v: ILimit) => v.color));

  const addAnnotation  = () => {
    const color = getPaletteColor();
    setColors([ ...colors, color]);


    setLimits([ ...limits, {
      title: "Limit",
      color: color,
      value: 0
    } ]);
  };

  const removeAnnotation  = (index: number) => {
    limits.splice(index, 1);
    setLimits([ ...limits ]);

    const newColors = [ ...colors];
    newColors.splice(index, 1);
    setColors(newColors);
  };

  const updateItemsAnnotation = (index: number, field: keyof ILimit, value: any) => {
    const item = limits[index];
    (item[field] as any) = value;

    if (field === "variable" && value) {
      item.title = value.name;
      item.variable = value.code;
    } else if (field === "color") {
      const newColors = [...colors];
      newColors[index] = value;
      setColors(newColors);
      item[field] = newColors[index];
    } else if (field === "type") {
      switch (value) {
        case "MIN":
          item.title = "Minimum";
          break;
        case "MAX":
          item.title = "Maximum";
          break;
        case "AVG":
          item.title = "Average";
          break;
        default:
          item.title = "Value";
          break;
      }
    }

    setLimits([ ...limits ]);
  };

  return (
    <TableContainer sx={{ maxHeight: 270 }}>
      <Table stickyHeader={true}  size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ minWidth: 180 }}>
              <IconButton sx={{ padding: 2 }} onClick={addAnnotation} disabled={limits.length >= 4}>
                <Tooltip title={"Add Limit"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
              Limit
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100, width: 100 }}>Type</TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Value</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}>Color</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}/>
          </TableRow>
        </TableHead>
        <TableBody>
          { limits.map((row, index) => {

            const found = machineVariables.find(x => row.variable && x.code === row.variable) ?? null;

            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                <TableCell>
                  <TextField
                    variant="standard"
                    size="small"
                    value={row.title}
                    onChange={(event) => updateItemsAnnotation(index, "title",  event.target.value as string)}
                  />
                </TableCell>

                <TableCell style={{ minWidth: 100, width: 100 }}>
                  <FormControl>
                    <Select
                      variant="standard"
                      value={row.type ?? "VALUE"}
                      displayEmpty={true}
                      onChange={(event) => updateItemsAnnotation(index, "type", event.target.value as string)}>
                      <MenuItem value={"VALUE"}>VALUE</MenuItem>
                      <MenuItem value={"MIN"}>MIN</MenuItem>
                      <MenuItem value={"MAX"}>MAX</MenuItem>
                      <MenuItem value={"AVG"}>AVG</MenuItem>
                      <MenuItem value={"VARIABLE"}>VARIABLE</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell>
                  { row.type !== "VARIABLE"
                    ? <FormControl className="w-100">
                      <TextField
                        variant="standard"
                        size="small"
                        value={row.value}
                        type="number"
                        disabled={row.type && row.type !== "VALUE"}
                        onChange={(event) => updateItemsAnnotation(index, "value", parseFloat(event.target.value as string))}
                      />
                    </FormControl>
                    : <Tooltip title={row.variable ?? "---"}>
                      <Autocomplete
                        options={machineVariables}
                        getOptionLabel={(option: IAcquisitionVariable) => option.name}
                        value={found}
                        openOnFocus={true}
                        onChange={(_event: any, newValue: IAcquisitionVariable | null) => {
                          updateItemsAnnotation(index, "variable", newValue);
                        }}
                        renderInput={(params) =>
                          <TextField {...params} variant="standard" color={"primary"}/>
                        }
                      />
                    </Tooltip>}
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    {/*<input type="color" value={row.color} onChange={(event: ChangeEvent<HTMLInputElement>) => updateItemsAnnotation(index, "color", event.target.value as string)}/>*/}
                    <ColorPicker color={colors[index]} onChange={(color) => updateItemsAnnotation(index, "color", color)} disableAlpha={true}/>
                  </FormControl>
                </TableCell>

                <TableCell>
                  <IconButton sx={{ padding: 2 }} onClick={() => removeAnnotation(index)} size={"small"} >
                    <Tooltip title={"Remove annotation"}>
                      <Delete style={{ color: getPalette().statusColor.error.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LimitsPanel;
